import { useEffect } from 'react';
import {
  ANNUAL_TIMETABLE_SAVED_EVENT,
  type AnnualTimetableSavedDetail,
} from '../../utils/annualTimetableSavedEvents';
import {
  PROGRAM_PROGRESS_SAVED_EVENT,
  type ProgramProgressSavedDetail,
} from '../../utils/programProgressSavedEvents';
import { applyAnnualTimetableSaveQueryUpdates } from '../cacheUpdates';
import { schoolKeys } from '../keys';
import { useQueryClient } from '@tanstack/react-query';

/**
 * 프로그램·연간시간표 저장 이벤트를 React Query invalidate로 연결.
 * MainLayout 또는 App에서 워크스페이스당 1회 마운트.
 */
export function useSchoolQueryInvalidation(workspaceId: string | undefined): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const ws = workspaceId?.trim();
    if (!ws) return undefined;

    const onProgramSaved = (event: Event) => {
      const detail = (event as CustomEvent<ProgramProgressSavedDetail>).detail;
      if (!detail?.workspaceId || detail.workspaceId !== ws || !detail.gradeId) return;
      /** patch(cache) 저장은 upsert 응답으로 이미 캐시를 맞췄으므로 전량 invalidate 생략 */
      if ((detail.uiRefreshMode ?? 'full') === 'cache') return;
      const { academicYear, termNumber, gradeId } = detail;
      void queryClient.invalidateQueries({
        queryKey: schoolKeys.programProgressCells(ws, academicYear, termNumber, gradeId),
      });
      void queryClient.invalidateQueries({
        queryKey: schoolKeys.programScopedRows(ws, academicYear, termNumber, gradeId),
      });
      void queryClient.invalidateQueries({
        queryKey: schoolKeys.programFixedRows(ws, academicYear, termNumber, gradeId),
      });
    };

    const onTimetableSaved = (event: Event) => {
      const detail = (event as CustomEvent<AnnualTimetableSavedDetail>).detail;
      if (!detail?.workspaceId || detail.workspaceId !== ws) return;
      const { academicYear, termNumbers, gradeIds } = detail;
      applyAnnualTimetableSaveQueryUpdates({
        workspaceId: ws,
        academicYear,
        termNumbers,
        gradeIds,
      });
    };

    window.addEventListener(PROGRAM_PROGRESS_SAVED_EVENT, onProgramSaved as EventListener);
    window.addEventListener(ANNUAL_TIMETABLE_SAVED_EVENT, onTimetableSaved as EventListener);
    return () => {
      window.removeEventListener(PROGRAM_PROGRESS_SAVED_EVENT, onProgramSaved as EventListener);
      window.removeEventListener(ANNUAL_TIMETABLE_SAVED_EVENT, onTimetableSaved as EventListener);
    };
  }, [queryClient, workspaceId]);
}

/** `useSchoolQueryInvalidation`을 선언적으로 마운트 */
export function SchoolQueryInvalidationHost({ workspaceId }: { workspaceId: string }) {
  useSchoolQueryInvalidation(workspaceId);
  return null;
}
